import { useState, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { map, find, compact, meanBy, keyBy } from 'lodash'
import dayjs from 'dayjs'
import Page from '../../../../shared/components/layouts/Page'
import PageHeader from '../../../../shared/components/layouts/PageHeader'
import PageBody from '../../../../shared/components/layouts/PageBody'
import Flex from '../../../../shared/components/layouts/Flex'
import { useNodes } from '../../../../contexts/NodesContext'
import { useAuthentication } from '../../../../contexts/AuthenticationContext'
import { TIME_RANGES } from '../../../../shared/constants/statisticsConfig'
import { API_HOST_NAME } from '../../../../shared/resources/api/v1/constants'
import fetchJSON from '../../../../shared/resources/fetchJSON'
import toUrlSearchParams from '../../../../shared/resources/toUrlSearchParams'
import NodeMetricCard from './NodeMetricCard'

const METRICS = [
  { value: 'cpu', labelId: 'monitoring.metrics.cpu' },
  { value: 'memory', labelId: 'monitoring.metrics.memory' },
  { value: 'disk', labelId: 'monitoring.metrics.disk' },
]

const DEFAULT_METRIC = 'cpu'
const DEFAULT_TIME_RANGE = '1h'

function MonitoringContent() {
  const { teamSlug } = useParams()
  const { nodes, loading: nodesLoading } = useNodes()
  const { getValidToken } = useAuthentication()
  const [metric, setMetric] = useState(DEFAULT_METRIC)
  const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE)
  const [initialLoading, setInitialLoading] = useState(true)
  const nodeOrderRef = useRef(null)
  const initialDataRef = useRef({})
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (nodesLoading || nodes.length === 0 || nodeOrderRef.current) {
      if (!nodesLoading && nodes.length === 0) {
        setInitialLoading(false)
      }
      return
    }

    const selectedRange = find(TIME_RANGES, { value: DEFAULT_TIME_RANGE })
    const after = dayjs().subtract(selectedRange.hours, 'hour').toISOString()
    const queryString = toUrlSearchParams({
      interval: selectedRange.interval,
      after,
      limit: selectedRange.limit,
    }).toString()

    getValidToken()
      .then((accessToken) => {
        const headers = {
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        }

        const fetches = map(nodes, (node) => {
          const baseUrl = `${API_HOST_NAME}/api/v1/admin/teams/${teamSlug}/nodes/${node.id}/statistics/${DEFAULT_METRIC}`
          const fullUrl = compact([baseUrl, queryString]).join('?')

          return fetchJSON(fullUrl, 'GET', { headers })
            .then((response) => ({
              nodeId: node.id,
              statistics: response.statistics || [],
            }))
            .catch(() => ({
              nodeId: node.id,
              statistics: [],
            }))
        })

        return Promise.all(fetches)
      })
      .then((results) => {
        if (!mountedRef.current) return

        const nodesById = keyBy(nodes, 'id')
        const nodeAverages = map(results, (result) => ({
          nodeId: result.nodeId,
          node: nodesById[result.nodeId],
          avgLoad: result.statistics.length > 0 ? meanBy(result.statistics, 'avg') : 0,
        }))

        nodeAverages.sort((a, b) => b.avgLoad - a.avgLoad)
        nodeOrderRef.current = map(nodeAverages, 'nodeId')

        const dataMap = {}
        results.forEach((result) => {
          dataMap[result.nodeId] = result.statistics
        })
        initialDataRef.current = dataMap

        setInitialLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current) return
        nodeOrderRef.current = map(nodes, 'id')
        setInitialLoading(false)
      })
  }, [nodesLoading, nodes, teamSlug, getValidToken])

  const handleMetricChange = useCallback((_event, newMetric) => {
    if (newMetric !== null) {
      setMetric(newMetric)
    }
  }, [])

  const handleTimeRangeChange = useCallback((event) => {
    setTimeRange(event.target.value)
  }, [])

  const nodesById = keyBy(nodes, 'id')
  const orderedNodes = nodeOrderRef.current
    ? compact(map(nodeOrderRef.current, (id) => nodesById[id]))
    : []

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="monitoring.title" />}
        actions={
          <Flex gap={2} alignItems="center">
            <TextField
              select
              size="small"
              value={timeRange}
              onChange={handleTimeRangeChange}
              label={<FormattedMessage id="monitoring.timeRangeLabel" />}
              sx={{ minWidth: 140 }}
            >
              {map(TIME_RANGES, (range) => (
                <MenuItem key={range.value} value={range.value}>
                  <FormattedMessage id={range.labelId} />
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              value={metric}
              exclusive
              onChange={handleMetricChange}
              size="small"
            >
              {map(METRICS, (m) => (
                <ToggleButton key={m.value} value={m.value}>
                  <FormattedMessage id={m.labelId} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Flex>
        }
      />
      <PageBody>
        {nodesLoading || initialLoading ? (
          <Flex justifyContent="center" alignItems="center" fullHeight>
            <CircularProgress />
          </Flex>
        ) : orderedNodes.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" fullHeight>
            <Typography color="text.secondary">
              <FormattedMessage id="monitoring.empty" />
            </Typography>
          </Flex>
        ) : (
          <Grid container spacing={3}>
            {map(orderedNodes, (node) => (
              <Grid key={node.id} size={{ xs: 12, lg: 6 }}>
                <NodeMetricCard
                  node={node}
                  metric={metric}
                  timeRange={timeRange}
                  initialData={initialDataRef.current[node.id]}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </PageBody>
    </Page>
  )
}

export default MonitoringContent
